import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronLeft, ChevronRight, RefreshCw, AlertCircle, Calendar } from 'lucide-react';
import IssueCard from './IssueCard';
import { fetchLiveIpos, fetchFeaturedIpos } from '../../store/slices/ipoSlice';

export const FeaturedIssues = () => {
  const dispatch = useDispatch();
  const {
    liveIposCurrent,
    liveIposUpcoming,
    liveIposLoading,
    liveIposError,
    featuredIpos,
  } = useSelector((state) => state.ipo);

  // Primary Tab: 'current' | 'upcoming'
  const [primaryTab, setPrimaryTab] = useState('upcoming');

  // Secondary Filter: 'All' | 'Mainboard' | 'SME'
  const [activeType, setActiveType] = useState('All');

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [activeDot, setActiveDot] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const sliderRef = useRef(null);

  // Fetch live market IPOs & core IPOs for registry mapping
  useEffect(() => {
    dispatch(fetchLiveIpos(false));
    if (!featuredIpos || featuredIpos.length === 0) {
      dispatch(fetchFeaturedIpos({ page: 1, page_size: 100 }));
    }
  }, [dispatch, featuredIpos]);

  // Active issues list based on primary tab
  const activeList = useMemo(() => {
    return primaryTab === 'upcoming' ? (liveIposUpcoming || []) : (liveIposCurrent || []);
  }, [primaryTab, liveIposCurrent, liveIposUpcoming]);

  // Secondary type filters with dynamic counts based on active list
  const typeFilters = useMemo(() => {
    const list = activeList || [];
    const allCount = list.length;
    const mbCount = list.filter((item) => item.series === 'EQ').length;
    const smeCount = list.filter((item) => item.series === 'SME').length;
    const otherCount = allCount - mbCount - smeCount;

    const filters = [
      { label: 'All', count: allCount, value: 'All' },
      { label: 'Mainboard', count: mbCount, value: 'Mainboard' },
      { label: 'SME IPOs', count: smeCount, value: 'SME' },
    ];
    if (otherCount > 0) {
      filters.push({ label: 'Other', count: otherCount, value: 'Other' });
    }
    return filters;
  }, [activeList]);

  // Filtered issues to display
  const filteredIssues = useMemo(() => {
    const list = activeList || [];
    if (activeType === 'SME') {
      return list.filter((item) => item.series === 'SME');
    }
    if (activeType === 'Mainboard') {
      return list.filter((item) => item.series === 'EQ');
    }
    if (activeType === 'Other') {
      return list.filter((item) => item.series !== 'EQ' && item.series !== 'SME');
    }
    return list;
  }, [activeList, activeType]);

  // Check scroll boundary
  const updateScrollState = () => {
    if (sliderRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);

      const maxScroll = scrollWidth - clientWidth;
      if (maxScroll > 0) {
        const progress = scrollLeft / maxScroll;
        const totalDots = 4;
        const currentDot = Math.min(
          totalDots - 1,
          Math.floor(progress * totalDots + 0.5)
        );
        setActiveDot(currentDot);
      } else {
        setActiveDot(0);
      }
    }
  };

  useEffect(() => {
    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener('scroll', updateScrollState, { passive: true });
      updateScrollState();
      return () => slider.removeEventListener('scroll', updateScrollState);
    }
  }, [filteredIssues]);

  // Switch primary tab and reset scroll
  const handlePrimaryTabChange = (tab) => {
    setPrimaryTab(tab);
    setActiveType('All');
    if (sliderRef.current) {
      sliderRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    }
  };

  // Switch secondary filter and reset scroll
  const handleTypeChange = (val) => {
    setActiveType(val);
    if (sliderRef.current) {
      sliderRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    }
  };

  const scrollPrev = () => {
    if (sliderRef.current) {
      const card = sliderRef.current.querySelector('[data-slider-card]');
      const scrollDistance = card ? card.offsetWidth + 24 : 320;
      sliderRef.current.scrollBy({ left: -scrollDistance, behavior: 'smooth' });
    }
  };

  const scrollNext = () => {
    if (sliderRef.current) {
      const card = sliderRef.current.querySelector('[data-slider-card]');
      const scrollDistance = card ? card.offsetWidth + 24 : 320;
      sliderRef.current.scrollBy({ left: scrollDistance, behavior: 'smooth' });
    }
  };

  // Auto-slide step
  const autoSlideNext = React.useCallback(() => {
    if (sliderRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
      const card = sliderRef.current.querySelector('[data-slider-card]');
      const scrollDistance = card ? card.offsetWidth + 24 : 320;

      if (scrollLeft + clientWidth >= scrollWidth - 25) {
        sliderRef.current.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        sliderRef.current.scrollBy({ left: scrollDistance, behavior: 'smooth' });
      }
    }
  }, []);

  // Auto-slide timer: moves forward every 3.5s, pauses on hover/touch
  useEffect(() => {
    if (isHovered || liveIposLoading || !filteredIssues || filteredIssues.length <= 1) {
      return;
    }

    const timer = setInterval(() => {
      autoSlideNext();
    }, 3500);

    return () => clearInterval(timer);
  }, [isHovered, liveIposLoading, filteredIssues, autoSlideNext]);

  const scrollToDot = (dotIndex) => {
    if (sliderRef.current) {
      const { scrollWidth, clientWidth } = sliderRef.current;
      const maxScroll = scrollWidth - clientWidth;
      const targetScroll = (maxScroll / 3) * dotIndex;
      sliderRef.current.scrollTo({ left: targetScroll, behavior: 'smooth' });
      setActiveDot(dotIndex);
    }
  };

  const handleRetry = () => {
    dispatch(fetchLiveIpos(true));
  };

  return (
    <section id="featured-issues" className="py-12 sm:py-16 border-t border-slate-100 bg-white">
      <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28">

        {/* Section Header: Title + Primary Current/Upcoming Tabs + Secondary Pills */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-2">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                Featured Issues
              </h2>
            </div>
            <p className="text-slate-500 text-xs sm:text-sm mt-1">
              Explore real-time market issues streamed directly from NSE live exchange feeds
            </p>
          </div>

          {/* Primary Tabs (Current vs Upcoming) & Secondary Filter Pills */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            {/* Primary Toggle: Current vs Upcoming */}
            <div className="inline-flex p-1 rounded-2xl bg-slate-100 border border-slate-200/80 shadow-xs">
              <button
                type="button"
                onClick={() => handlePrimaryTabChange('current')}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all ${primaryTab === 'current'
                  ? 'bg-white text-[#008080] shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
                  }`}
              >
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                <span>Current Issues</span>
                <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold ml-0.5">
                  {liveIposCurrent?.length || 0}
                </span>
              </button>

              <button
                type="button"
                onClick={() => handlePrimaryTabChange('upcoming')}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all ${primaryTab === 'upcoming'
                  ? 'bg-white text-[#008080] shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
                  }`}
              >
                <Calendar className="w-3.5 h-3.5 text-slate-500" />
                <span>Upcoming</span>
                <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold ml-0.5">
                  {liveIposUpcoming?.length || 0}
                </span>
              </button>
            </div>

            {/* Secondary Type Filter Pills: All, Mainboard, SME */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0 scrollbar-none">
              {typeFilters.map((flt) => {
                const isActive = activeType === flt.value;
                return (
                  <button
                    key={flt.value}
                    type="button"
                    onClick={() => handleTypeChange(flt.value)}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${isActive
                      ? 'bg-[#008080] text-white shadow-sm shadow-[#008080]/25'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900'
                      }`}
                  >
                    <span>{flt.label}</span>
                    <span
                      className={`text-[10px] px-1.5 py-0.2 rounded-full ${isActive ? 'bg-white/25 text-white' : 'bg-slate-200 text-slate-700'
                        }`}
                    >
                      {flt.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Error State */}
        {liveIposError && !liveIposLoading && (
          <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-3 text-xs sm:text-sm">
            <div className="flex items-center gap-2.5 text-amber-800">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
              <span>{liveIposError}</span>
            </div>
            <button
              type="button"
              onClick={handleRetry}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-semibold shadow-xs transition-colors shrink-0"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Retry</span>
            </button>
          </div>
        )}

        {/* Carousel Slider Container with Auto-slide pause on hover */}
        <div
          className="relative mt-8 group/slider"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onTouchStart={() => setIsHovered(true)}
          onTouchEnd={() => setIsHovered(false)}
        >
          {/* Previous Arrow Button */}
          <button
            type="button"
            onClick={scrollPrev}
            disabled={!canScrollLeft || liveIposLoading}
            className={`absolute -left-3 sm:-left-5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center transition-all ${canScrollLeft && !liveIposLoading
              ? 'text-slate-700 hover:text-[#008080] hover:border-teal-300 hover:scale-110 active:scale-95 cursor-pointer opacity-95 hover:opacity-100'
              : 'text-slate-300 border-slate-100 opacity-0 pointer-events-none'
              }`}
            aria-label="Previous issues"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Next Arrow Button */}
          <button
            type="button"
            onClick={scrollNext}
            disabled={!canScrollRight || liveIposLoading}
            className={`absolute -right-3 sm:-right-5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center transition-all ${canScrollRight && !liveIposLoading
              ? 'text-slate-700 hover:text-[#008080] hover:border-teal-300 hover:scale-110 active:scale-95 cursor-pointer opacity-95 hover:opacity-100'
              : 'text-slate-300 border-slate-100 opacity-0 pointer-events-none'
              }`}
            aria-label="Next issues"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Loading Skeleton Slider */}
          {liveIposLoading ? (
            <div className="flex items-stretch gap-5 sm:gap-6 overflow-hidden py-2 px-1">
              {[1, 2, 3, 4].map((n) => (
                <div
                  key={n}
                  className="shrink-0 w-[85vw] max-w-[320px] sm:w-[calc(50%-12px)] sm:max-w-none lg:w-[calc(33.333%-16px)] xl:w-[calc(25%-18px)] rounded-2xl border border-slate-200/90 p-5 bg-white animate-pulse space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="w-9 h-9 rounded-xl bg-slate-200" />
                    <div className="w-16 h-5 rounded-full bg-slate-100" />
                  </div>
                  <div className="w-3/4 h-5 rounded bg-slate-200" />
                  <div className="h-16 rounded-xl bg-slate-100" />
                  <div className="space-y-2 pt-2">
                    <div className="w-full h-3.5 rounded bg-slate-100" />
                    <div className="w-full h-3.5 rounded bg-slate-100" />
                    <div className="w-2/3 h-3.5 rounded bg-slate-100" />
                  </div>
                  <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                    <div className="w-12 h-4 rounded bg-slate-100" />
                    <div className="w-20 h-4 rounded bg-slate-200" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredIssues && filteredIssues.length > 0 ? (
            /* Smooth Horizontal Carousel Track with Snap */
            <div
              ref={sliderRef}
              className="flex items-stretch gap-5 sm:gap-6 overflow-x-auto scrollbar-none snap-x snap-mandatory py-2 px-1 scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {filteredIssues.map((issue, idx) => (
                <div
                  key={issue.symbol || issue.companyName || idx}
                  data-slider-card
                  className="shrink-0 snap-start w-[85vw] max-w-[320px] sm:w-[calc(50%-12px)] sm:max-w-none lg:w-[calc(33.333%-16px)] xl:w-[calc(25%-18px)]"
                >
                  <IssueCard issue={issue} coreRegistry={featuredIpos} />
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center bg-slate-50 border border-slate-100 rounded-3xl text-slate-500 space-y-2">
              <p className="font-bold text-slate-700 text-sm">
                No {activeType === 'All' ? '' : activeType} issues currently active in this category
              </p>
              <p className="text-xs text-slate-400">
                New issues will appear automatically when announced on the exchange.
              </p>
            </div>
          )}

          {/* Pagination Indicator Dots */}
          {filteredIssues && filteredIssues.length > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              {[0, 1, 2, 3].map((dot) => (
                <button
                  key={dot}
                  type="button"
                  onClick={() => scrollToDot(dot)}
                  className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${activeDot === dot
                    ? 'w-7 bg-[#008080] shadow-xs shadow-[#008080]/30'
                    : 'w-2 bg-slate-200 hover:bg-slate-300'
                    }`}
                  aria-label={`Go to slide ${dot + 1}`}
                />
              ))}
            </div>
          )}
        </div>

      </div>
    </section>
  );
};

export default FeaturedIssues;
